import { Component, EventEmitter, Output, Input, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { WellDataApiService } from '~/services/well-data-api.service';

interface WellOption {
  id: string;      // WellID
  name: string;    // WellName
  field?: string;  // Field name
}

@Component({
  selector: 'app-prompt-selection',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './prompt-selection.html',
  styleUrls: ['./prompt-selection.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PromptSelectionComponent implements OnInit {
  @Output() promptSelected = new EventEmitter<{text: string; type: string; promptId?: string; selectedWells?: string[]; selectedFields?: string[]}>();
  @Output() loadingChanged = new EventEmitter<boolean>();
  @Input() parentComponent: any;

  // Well-level prompts: NO wellId — wellId comes from backend query API response
  wellLevelPrompts: { id: string; label: string }[] = [
    { id: 'prompt-vulcan-196r', label: 'Generate the complete P&A lookback report for Vulcan 196R' },
    { id: 'prompt-usl-32-164a', label: 'Show planned vs actual P&A duration and cost variance for well Usl 32 164A' },
    { id: 'prompt-sec-21-q190', label: 'Give NPT details for well Sec 21 South Q190' },
    { id: 'prompt-usl-32-10-10uw', label: 'What were the top NPT drivers for well Usl 32 10-10UW and their cost impact?' },
  ];

  fieldLevelPrompts: { id: string; label: string }[] = [
    { id: 'field_cost_impact', label: 'Give me NPT cost and duration breakdown for all fields in SJV' },
    { id: 'field_npt_events_1yr', label: 'Give top 3 fields which have the highest NPT costs for abandonment operations' },
    { id: 'field_risk',        label: 'Give me the average cost deviation by fields for wells abandoned in the San Joaquin Valley' },
    { id: 'field_summary',     label: 'Compare schedule deviation for wells abandoned in Q1 2025 vs Q2 2025 for the field "Lost Hills"' },
  ];

  // Multi-well dropdown list — will be populated from API (stores both ID and Name)
  multiWellList: WellOption[] = [];

  // Field configuration
  fieldList: string[] = [];
  loadingFields = false;
  
  // Component state
  wellMode: 'single' | 'multi' = 'single';
  inputValue = '';
  selectedPromptType: string | null = null;
  selectedPromptId: string | undefined = undefined;
  selectedWellIds: string[] = [];  // Store well IDs, not names
  selectedFields: string[] = [];  // Finalized multi-select fields (confirmed via OK button)
  tempSelectedFields: string[] = [];  // Temporary field selection (before confirming with OK button)
  inputMode: 'single' | 'multi' | 'field' = 'single';
  multiWellSearch = '';
  fieldDropdownOpen = false;
  multiDropdownOpen = false;
  isLoading = false;  // Loading state for query API
  loadingWells = false;  // Loading state for wells API
  wellSelectionError = '';  // Error message for well selection limit

  constructor(
    private cdr: ChangeDetectorRef,
    private wellDataApi: WellDataApiService
  ) {}

  get filteredMultiWells(): WellOption[] {
    if (!this.multiWellSearch.trim()) return this.multiWellList;
    const lower = this.multiWellSearch.toLowerCase();
    return this.multiWellList.filter(w => w.name.toLowerCase().includes(lower));
  }

  ngOnInit() {
    // Load fields from API
    this.loadFieldsFromApi();
    
    // Load conversations when this component initializes (user navigated to prompt page)
    if (this.parentComponent && typeof this.parentComponent.loadConversations === 'function') {
      this.parentComponent.loadConversations();
    }
  }

  /**
   * Load fields from API: GET /api/v1/fields
   */
  loadFieldsFromApi(): void {
    this.loadingFields = true;
    this.cdr.markForCheck();
    this.wellDataApi.getFieldsList().subscribe({
      next: (response: any) => {
        // Assuming response has a 'data' array with field names
        this.fieldList = response?.data || [];
        this.loadingFields = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        // Fallback to empty list if API fails
        this.fieldList = [];
        this.loadingFields = false;
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Load wells from API for the given field(s).
   * Calls GET /api/v1/wells-list?field_name=X for single field
   * Calls POST /api/v1/wells-list with array in body for multiple fields
   * Expected response format: { data: [{WellID: "...", WellName: "...", FieldName: "..."}, ...] }
   */
  loadWellsForFields(fields: string[]): void {
    if (!fields || fields.length === 0) {
      this.multiWellList = [];
      this.loadingWells = false;
      this.selectedWellIds = [];
      this.multiWellSearch = '';
      this.cdr.markForCheck();
      return;
    }
    
    this.loadingWells = true;
    this.selectedWellIds = [];  // Reset selection when field changes
    this.multiWellSearch = '';
    
    // Call API with all selected fields (single call for all fields)
    this.wellDataApi.getWellsList(fields).subscribe({
      next: (response: any) => {
        // Extract well IDs and names from API response
        const wells = response?.data || [];
        this.multiWellList = wells.map((w: any) => ({
          id: w.WellID,
          name: w.WellName || w.WellID,
          field: w.FieldName || w.Field || ''  // Capture field name
        })).filter((w: WellOption) => w.id && w.name);
        this.loadingWells = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.multiWellList = [];
        this.loadingWells = false;
        this.cdr.markForCheck();
      }
    });
  }

  toggleFieldSelection(field: string) {
    if (this.tempSelectedFields.includes(field)) {
      this.tempSelectedFields = this.tempSelectedFields.filter(f => f !== field);
    } else {
      this.tempSelectedFields.push(field);
    }
    this.cdr.markForCheck();
  }

  /**
   * Toggle select all fields
   */
  toggleSelectAllFields() {
    if (this.tempSelectedFields.length === this.fieldList.length) {
      // Deselect all
      this.tempSelectedFields = [];
    } else {
      // Select all
      this.tempSelectedFields = [...this.fieldList];
    }
    this.cdr.markForCheck();
  }

  isFieldSelected(field: string): boolean {
    return this.tempSelectedFields.includes(field);
  }

  /**
   * Confirm field selection and load wells for all selected fields
   * This is called when user clicks OK button in field dropdown
   */
  confirmFieldSelection() {
    if (this.tempSelectedFields.length === 0) {
      // Clear everything if no fields selected
      this.selectedFields = [];
      this.multiWellList = [];
      this.selectedWellIds = [];
      this.multiWellSearch = '';
      this.fieldDropdownOpen = false;
      this.cdr.markForCheck();
      return;
    }
    
    // Confirm the selection and load wells
    this.selectedFields = [...this.tempSelectedFields];
    this.loadWellsForFields(this.selectedFields);
    this.fieldDropdownOpen = false;
    this.cdr.markForCheck();
  }

  /**
   * Cancel field selection without confirming
   */
  cancelFieldSelection() {
    this.tempSelectedFields = this.selectedFields.slice();  // Reset to previously confirmed fields
    this.fieldDropdownOpen = false;
    this.cdr.markForCheck();
  }

  /**
   * Open field dropdown and reset tempSelectedFields to current selectedFields
   */
  openFieldDropdown() {
    this.tempSelectedFields = this.selectedFields.slice();
    this.fieldDropdownOpen = !this.fieldDropdownOpen;
    this.multiDropdownOpen = false;
    this.cdr.markForCheck();
  }

  setWellMode(mode: 'single' | 'multi') {
    this.wellMode = mode;
    this.selectedWellIds = [];
    this.multiWellSearch = '';
    this.cdr.markForCheck();
  }

  isWellSelected(wellId: string): boolean {
    return this.selectedWellIds.includes(wellId);
  }

  toggleWellSelection(wellId: string) {
    if (this.selectedWellIds.includes(wellId)) {
      this.selectedWellIds = this.selectedWellIds.filter(w => w !== wellId);
      this.wellSelectionError = '';  // Clear error on deselection
    } else {
      // Restrict selection to maximum 30 wells
      if (this.selectedWellIds.length < 30) {
        this.selectedWellIds.push(wellId);
        this.wellSelectionError = '';  // Clear error on successful selection
      } else {
        // Set error message
        this.wellSelectionError = 'Maximum 30 wells can be selected';
      }
    }
    // Clear search text after selecting a well
    this.multiWellSearch = '';
    this.cdr.markForCheck();
  }

  /**
   * Toggle select all wells (only visible wells from current filter)
   */
  toggleSelectAll() {
    const visibleWellIds = this.filteredMultiWells.map(w => w.id);
    
    // If all visible wells are selected, deselect all
    // Otherwise, select all visible wells (up to maximum 30)
    if (visibleWellIds.every(id => this.selectedWellIds.includes(id))) {
      // Deselect all visible wells, but keep non-visible ones selected
      this.selectedWellIds = this.selectedWellIds.filter(id => !visibleWellIds.includes(id));
      this.wellSelectionError = '';  // Clear error on deselection
    } else {
      // Add all visible wells to selection (keep existing ones, respect 30-well limit)
      const newSelection = new Set(this.selectedWellIds);
      let addedCount = 0;
      let exceededLimit = false;
      
      for (const id of visibleWellIds) {
        if (!newSelection.has(id)) {
          if (newSelection.size < 30) {
            newSelection.add(id);
            addedCount++;
          } else {
            exceededLimit = true;
            break;
          }
        }
      }
      
      this.selectedWellIds = Array.from(newSelection);
      
      // Set error message if limit was exceeded
      if (exceededLimit) {
        this.wellSelectionError = 'Maximum 30 wells can be selected';
      } else {
        this.wellSelectionError = '';
      }
    }
    
    // Clear search text after selecting
    this.multiWellSearch = '';
    this.cdr.markForCheck();
  }

  removeWell(wellId: string) {
    this.selectedWellIds = this.selectedWellIds.filter(w => w !== wellId);
    this.wellSelectionError = '';  // Clear error when removing a well
    // Clear search text after removing a well
    this.multiWellSearch = '';
    this.cdr.markForCheck();
  }

  getWellNameById(wellId: string): string {
    const well = this.multiWellList.find(w => w.id === wellId);
    return well ? well.name : wellId;
  }

  handlePromptClick(prompt: any, type: string) {
    this.inputValue = prompt.label;
    this.selectedPromptType = type;
    this.selectedPromptId = prompt.id;
    this.cdr.markForCheck();
  }

  onInputChange() {
    // Called when user types in multi-well input
    // filteredMultiWells getter handles filtering
    this.cdr.markForCheck();
  }

  /**
   * Handle pasting multiple well names into the multi-well search input
   * Supports common separators: comma, semicolon, newline, tab
   * Example: "Well-1, Well-2; Well-3"
   */
  handlePasteWells(event: ClipboardEvent) {
    // Only handle paste in multi-well mode
    if (this.wellMode !== 'multi') {
      return;
    }

    event.preventDefault();
    
    // Get pasted text from clipboard
    const pastedText = event.clipboardData?.getData('text') || '';
    
    if (!pastedText.trim()) {
      return;
    }

    // Parse multiple well names (split by comma, semicolon, newline, or tab)
    const wellNames = pastedText
      .split(/[,;\n\t]+/)  // Split by common separators
      .map(name => name.trim())  // Remove leading/trailing whitespace
      .filter(name => name.length > 0);  // Remove empty entries

    if (wellNames.length === 0) {
      return;
    }

    // Find matching well IDs from the available list
    const matchedWellIds: string[] = [];
    const unmatchedNames: string[] = [];

    for (const pastedName of wellNames) {
      // Normalize both pasted name and well names for comparison
      const normalizedPastedName = this.normalizeWellName(pastedName);
      
      let matchedWell = this.multiWellList.find(w => 
        this.normalizeWellName(w.name) === normalizedPastedName
      );

      // If exact normalized match not found, try partial/fuzzy match
      if (!matchedWell) {
        matchedWell = this.multiWellList.find(w => 
          w.name.toLowerCase().includes(pastedName.toLowerCase()) ||
          pastedName.toLowerCase().includes(w.name.toLowerCase())
        );
      }

      if (matchedWell) {
        // Only add if not already selected
        if (!this.selectedWellIds.includes(matchedWell.id)) {
          matchedWellIds.push(matchedWell.id);
        }
      } else {
        unmatchedNames.push(pastedName);
      }
    }

    // Add matched wells to selection (respect 30-well limit)
    const newSelection = new Set(this.selectedWellIds);
    let addedCount = 0;
    let exceededLimit = false;

    for (const wellId of matchedWellIds) {
      if (newSelection.size < 30) {
        newSelection.add(wellId);
        addedCount++;
      } else {
        exceededLimit = true;
        break;
      }
    }

    this.selectedWellIds = Array.from(newSelection);

    // Clear search input after pasting
    this.multiWellSearch = '';

    // Set appropriate error/info messages
    if (exceededLimit || newSelection.size >= 30) {
      this.wellSelectionError = `Maximum 30 wells reached. Added ${addedCount} of ${matchedWellIds.length} wells.`;
    } else if (unmatchedNames.length > 0) {
      this.wellSelectionError = `Could not find: ${unmatchedNames.join(', ')}. Added ${addedCount} wells.`;
    } else if (addedCount > 0) {
      this.wellSelectionError = '';  // Clear any previous errors
    }

    this.cdr.markForCheck();
  }

  /**
   * Normalize well name for comparison
   * Removes/replaces special characters, converts to lowercase
   * Handles en-dashes, em-dashes, hyphens, spaces, etc.
   */
  private normalizeWellName(name: string): string {
    return name
      .toLowerCase()
      .trim()
      // Replace all types of dashes/hyphens with standard hyphen
      .replace(/[\u2010\u2011\u2012\u2013\u2014\-]/g, '-')
      // Remove extra spaces
      .replace(/\s+/g, ' ')
      // Remove any other special characters except alphanumeric, space, and hyphen
      .replace(/[^\w\s\-]/g, '');
  }

  cancelMultiWell() {
    this.selectedWellIds = [];
    this.inputValue = '';
    this.inputMode = 'single';
    this.multiWellSearch = '';
    this.wellMode = 'single';
    this.cdr.markForCheck();
  }

  /**
   * Clear all selected wells and fields
   */
  clearAllSelections() {
    this.selectedWellIds = [];
    this.selectedFields = [];
    this.tempSelectedFields = [];
    this.multiWellList = [];
    this.multiWellSearch = '';
    this.fieldDropdownOpen = false;
    this.multiDropdownOpen = false;
    this.cdr.markForCheck();
  }

  generateMultiWell() {
    if (this.selectedWellIds.length > 0 && !this.isLoading) {
      this.isLoading = true;
      this.loadingChanged.emit(true);
      // Build well names for display
      const wellNames = this.selectedWellIds.map(id => this.getWellNameById(id));
      this.promptSelected.emit({
        text: `Multi-well NPT Summary for: ${wellNames.join(', ')}`,
        type: 'multi-well',
        selectedWells: this.selectedWellIds,  // Send IDs to parent
        selectedFields: this.selectedFields,  // Send field names array
        promptId: undefined,
      });
    }
  }

  handleSubmit() {
    if (this.inputValue.trim() && !this.isLoading) {
      this.isLoading = true;
      this.loadingChanged.emit(true);
      
      // If in multi-well mode, include selected well IDs in the event
      const eventData: any = {
        text: this.inputValue,
        type: this.selectedPromptType || 'well',
        promptId: this.selectedPromptId,
      };
      
      if (this.wellMode === 'multi' && this.selectedWellIds.length > 0) {
        eventData.selectedWells = this.selectedWellIds;  // Send IDs to parent
        eventData.selectedFields = this.selectedFields;  // Send field names array
        eventData.type = 'multi-well';
      }
      
      this.promptSelected.emit(eventData);
    }
  }

  // Call this from parent when query is complete to reset loading state
  resetLoading() {
    this.isLoading = false;
    this.loadingChanged.emit(false);
    this.cdr.markForCheck();
  }
}

